/**
 * POS
 */

import React from 'react'
import {
	AppBar,
	Box,
	Button,
	Dialog,
	Divider,
	Grid,
	IconButton,
	Paper,
	Tab,
	Tabs,
	TextField,
	Toolbar,
	Typography,
} from '@mui/material'
/** TYPES */
import type { User } from '@/types/user'
/** ICONS */
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import usePOS from '@/hooks/pos.hooks'
import PincodeDialog from './pincode.dialog'
import { useSnackbar } from 'notistack'
import POSCart from './pos.cart'
import POSInvoices from './pos.invoices'
import { CartItem, PosPayment } from '@/types/pos'
import POSServices from './pos.services'
import POSProducts from './pos.products'

interface POSProps {
	open: boolean
	onClose: () => void
}

const POS = (props: POSProps) => {
	const [open, setOpen] = React.useState(false)
	const [tabValue, setTabValue] = React.useState<string>('invoices')
	const [cartItems, setCartItems] = React.useState<CartItem[]>([])

	const [payments, setPayments] = React.useState<PosPayment[]>([])
	const [activeInvoiceId, setActiveInvoiceId] = React.useState<string | null>(null)

	const { fetchAuthorization, authorized, error } = usePOS()
	const snackbar = useSnackbar()

	React.useEffect(() => {
		if (error) {
			snackbar.enqueueSnackbar(error, { variant: 'error', autoHideDuration: 3000 })
		}
	}, [error, snackbar])

	React.useEffect(() => {
		if (props.open) {
			setOpen(true)
		} else {
			setOpen(false)
		}
	}, [props.open])

	const handleClose = () => {
		setOpen(false)
		setTimeout(() => {
			props.onClose()
		}, 350)
	}

	const totals = React.useMemo(() => {
		let subtotal = 0
		let discountTotal = 0
		let vatAmount = 0
		let totalPayable = 0

		cartItems.forEach((item) => {
			subtotal += item.unitPrice ? item.unitPrice * item.quantity : item.price * item.quantity
			discountTotal += item.discountedAmount ? item.discountedAmount : 0
		})

		const vatRate = 0.1 // Assuming a VAT rate of 10%

		vatAmount = Number(((subtotal - discountTotal) * vatRate).toFixed(2))
		totalPayable = Number((subtotal - discountTotal + vatAmount).toFixed(2))

		return {
			subtotal: Number(subtotal.toFixed(2)),
			discountTotal: Number(discountTotal.toFixed(2)),
			vatAmount,
			totalPayable,
		}
	}, [cartItems])

	const totalPaid = React.useMemo(() => {
		return payments.reduce((sum, payment) => sum + payment.amount, 0)
	}, [payments])

	const remainingAmount = React.useMemo(() => {
		return Number((totals.totalPayable - totalPaid).toFixed(2))
	}, [totals.totalPayable, totalPaid])

	const handleAddToCart = React.useCallback((items: CartItem[]) => {
		setCartItems((prevItems) => {
			const updatedItems = [...prevItems]

			items.forEach((newItem) => {
				// Find if item is already in cart
				const existingItemIndex = updatedItems.findIndex((item) => item.id === newItem.id)

				if (existingItemIndex > -1) {
					// If it's already there, don't add it again!
					// Or update quantity if that's what you prefer:
					// updatedItems[existingItemIndex].quantity += newItem.quantity;
				} else {
					// Only add if it's truly a new item
					updatedItems.push(newItem)
				}
			})

			return updatedItems
		})
	}, [])

	const handleInvoiceChange = React.useCallback((id: string | null) => {
		setActiveInvoiceId(id)
	}, [])

	const handleRemoveFromCart = (itemId: string | number) => {
		// if it's a number, remove by index
		if (typeof itemId === 'number') {
			setCartItems((prevItems) => {
				const updatedItems = [...prevItems]
				updatedItems.splice(itemId, 1)
				return updatedItems
			})
			return
		}

		// if it's a string, remove by id
		setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId))
	}

	const handleSelectInvoiceItems = React.useCallback((items: CartItem[]) => {
		setCartItems(items)
	}, [])

	const updateCartItems = (items: CartItem[]) => {
		setCartItems(items)
	}

	React.useEffect(() => {
		// remove payments if cart item changed
		setPayments([])
	}, [cartItems])

	React.useEffect(() => {
		console.log('Active Invoice ID changed:', activeInvoiceId)
	}, [activeInvoiceId])

	return (
		<Dialog fullScreen open={open} onClose={handleClose}>
			<PincodeDialog
				open={!authorized}
				onClose={handleClose}
				onSubmit={async (pincode: string) => {
					const success = await fetchAuthorization(pincode)
				}}
			/>
			<Box
				sx={{
					// blur background when not authorized
					filter: authorized ? 'none' : 'blur(4px)',
					pointerEvents: authorized ? 'auto' : 'none',
					height: '100vh',
					overflow: 'hidden',
					flexDirection: 'row',
					display: 'flex',
				}}
			>
				<Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
					<AppBar
						position="static"
						variant="outlined"
						sx={{
							boxShadow: 'none',
							background: 'none',
							border: 0,
							borderBottom: 1,
							borderColor: 'divider',
						}}
					>
						<Toolbar sx={{ bgcolor: (theme) => (theme.palette.mode === 'light' ? 'white' : 'grey.900') }}>
							<IconButton>
								<CloseIcon onClick={handleClose} />
							</IconButton>
							<Typography
								variant="h6"
								component="div"
								sx={{
									flexGrow: 1,
									marginLeft: 2,
									color: (theme) => (theme.palette.mode === 'light' ? 'black!important' : 'white'),
								}}
							>
								POS Terminal
							</Typography>
						</Toolbar>
					</AppBar>
					<Box>
						<Tabs
							value={tabValue}
							onChange={(e, newValue) => setTabValue(newValue)}
							aria-label="POS Tabs"
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
							}}
						>
							<Tab label="Нэхэмжлэлүүд" value="invoices" />
							<Tab label="Үйлчилгээнүүд" value="services" />
							<Tab label="Бүтээгдэхүүн" value="products" />
						</Tabs>
					</Box>
					<Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
						<Box padding={2} sx={{ display: tabValue === 'invoices' ? 'block' : 'none' }}>
							<POSInvoices
								onAddToCart={handleSelectInvoiceItems}
								onInvoiceChange={handleInvoiceChange}
								activeInvoiceId={activeInvoiceId}
							/>
						</Box>
						{tabValue === 'services' && (
							<Box padding={2}>
								<POSServices addItemToCart={handleAddToCart} />
							</Box>
						)}
						{tabValue === 'products' && <POSProducts addItemToCart={handleAddToCart} />}
					</Box>
				</Box>
				{/* RIGHT COLUMN: The Receipt / Cart */}
				<Paper
					square
					elevation={1}
					sx={{
						width: 450, // Slightly wider for better POS usability
						height: '100vh',
						display: 'flex',
						flexDirection: 'column',
						borderLeft: '1px solid',
						borderColor: 'divider',
						zIndex: (theme) => theme.zIndex.appBar + 1,
					}}
				>
					<POSCart
						cartItems={cartItems}
						onCheckout={() => {}}
						totals={totals}
						totalAmount={0}
						payments={payments}
						onPaymentsChange={(payments) => {
							setPayments(payments)
						}}
						remainingAmount={remainingAmount}
						onUpdateCart={(items) => {
							setCartItems(items)
						}}
						onDelete={handleRemoveFromCart}
					/>
				</Paper>
			</Box>
		</Dialog>
	)
}

export default POS
